// Normalizes an IIL segment's `summary` object, built by summarizeRows() in
// src/components/IILInvoice.js: { salaryAmount, otAmount, pessi, eobi,
// totalCost, serviceCharges, wht, invoiceAmount, gst, totalInvoiceValue }.
//
// `wht` here is the 8.8376% gross-up add-back already embedded in the invoice
// (IILInvoice.js:688, "With Holding Tax" column) — NOT the flat 7% deduction
// recorded at approval time (IILInvoice.js:1479). Both are distinct numbers;
// see whtCalculator.js's module comment. This adapter reports the embedded
// gross-up as a WHT_RECOVERY revenue line (it was billed to the client) and
// as `recoveryAddBack`/`recoveryBase` for the snapshot's variance check —
// the flat-7% deduction itself is computed separately by invoiceSnapshotService
// using the client's active tax profile, not duplicated here.
import { LINE_CATEGORIES, makeLine } from "../lineCategories";

export function normalizeIilInvoice(summary) {
  const salaryAmount = Number(summary.salaryAmount || 0);
  const otAmount = Number(summary.otAmount || 0);
  const pessi = Number(summary.pessi || 0);
  const eobi = Number(summary.eobi || 0);
  const totalCost = Number(summary.totalCost || 0);
  const serviceCharges = Number(summary.serviceCharges || 0);
  const wht = Number(summary.wht || 0);
  const invoiceAmount = Number(summary.invoiceAmount || 0);
  const gstAmount = Number(summary.gst || 0);
  const grossAmount = Number(summary.totalInvoiceValue || 0);

  // Education cess, group life insurance, and (for packer segments) transport
  // cost are folded into totalCost but not individually accumulated in
  // summary — recover the residual into SALARY_REIMBURSEMENT (paid out with
  // salary, same as overtime) rather than its own opaque bucket.
  const otherEmployeeRecovery = Math.max(0, totalCost - salaryAmount - otAmount - pessi - eobi);

  const lines = [
    makeLine({ category: LINE_CATEGORIES.SALARY_REIMBURSEMENT, description: "Salary reimbursement", amount: salaryAmount, sourceField: "salaryAmount" }),
    makeLine({ category: LINE_CATEGORIES.SALARY_REIMBURSEMENT, description: "Overtime recovery", amount: otAmount, sourceField: "otAmount" }),
    makeLine({ category: LINE_CATEGORIES.PESSI_RECOVERY, description: "PESSI recovery", amount: pessi, sourceField: "pessi" }),
    makeLine({ category: LINE_CATEGORIES.EOBI_RECOVERY, description: "EOBI recovery", amount: eobi, sourceField: "eobi" }),
    makeLine({ category: LINE_CATEGORIES.SALARY_REIMBURSEMENT, description: "Education cess, GLI, transport recovery", amount: otherEmployeeRecovery, sourceField: "totalCost residual" }),
    makeLine({ category: LINE_CATEGORIES.SERVICE_CHARGE, description: "Service charges", amount: serviceCharges, sourceField: "serviceCharges" }),
    makeLine({ category: LINE_CATEGORIES.WHT_RECOVERY, description: "WHT gross-up recovery (embedded)", amount: wht, sourceField: "wht" }),
    makeLine({ category: LINE_CATEGORIES.GST, description: "GST", amount: gstAmount, sourceField: "gst" })
  ];

  return {
    lines,
    serviceChargeSubtotal: serviceCharges,
    invoiceAmount,
    gstAmount,
    grossAmount,
    // Target amount ALM intended to net after the client's WHT deduction —
    // matches IILInvoice.js:729 `segmentBase = totalCost + serviceCharges`.
    recoveryBase: totalCost + serviceCharges,
    recoveryAddBack: wht
  };
}
